import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, ShieldCheck, ShieldAlert, ExternalLink, Share2 } from "lucide-react";
import { resolveLogo } from "@/lib/branding";
import { toast } from "sonner";

export default function VerifyCertificate() {
    const { certId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/verify/${certId}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [certId]);

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard");
    };

    if (loading) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center text-muted-foreground">
                Verifying…
            </div>
        );
    }

    if (!data?.valid) {
        return (
            <div className="max-w-xl mx-auto px-6 py-16" data-testid="verify-invalid">
                <Card className="border-destructive/40">
                    <CardContent className="p-10 text-center">
                        <ShieldAlert className="h-12 w-12 text-destructive mx-auto" />
                        <h1 className="font-display text-3xl text-primary mt-6">
                            Certificate not found
                        </h1>
                        <p className="text-muted-foreground mt-3">
                            The certificate ID <span className="font-mono">{certId}</span> could
                            not be verified. It may have been revoked or mistyped.
                        </p>
                        <Button asChild variant="outline" className="mt-6">
                            <Link to="/">Return to catalog</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const issuedDate = new Date(data.issued_at);
    const logo = resolveLogo(data.institution_logo);

    return (
        <div className="max-w-4xl mx-auto px-6 py-14" data-testid="verify-valid">
            <div className="text-center mb-8">
                <Badge className="bg-accent/15 text-accent border border-accent/40 gap-2 px-3 py-1 uppercase tracking-widest">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Verified Credential
                </Badge>
            </div>

            <Card className="border-border overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-primary via-accent to-primary" />
                <CardContent className="p-10 md:p-14 text-center">
                    {logo && (
                        <img
                            src={logo}
                            alt=""
                            className="h-16 w-16 mx-auto rounded-full object-contain"
                        />
                    )}
                    <div className="text-xs uppercase tracking-[0.28em] text-accent font-semibold mt-6">
                        {data.institution_name}
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl text-primary mt-3">
                        Certificate of Completion
                    </h1>
                    <div className="w-20 h-[2px] bg-accent mx-auto mt-5" />

                    <div className="mt-10 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                        Awarded to
                    </div>
                    <div className="font-display text-3xl md:text-4xl text-primary mt-3 italic">
                        {data.student_name}
                    </div>

                    <div className="mt-8 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                        for successfully completing
                    </div>
                    <div className="font-display text-2xl md:text-3xl text-foreground mt-2">
                        {data.program_title}
                    </div>
                    {data.program_subject && (
                        <Badge variant="outline" className="capitalize mt-3 border-border">
                            {data.program_subject}
                        </Badge>
                    )}

                    <div className="mt-10 grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
                        <Field label="Issued" value={issuedDate.toLocaleDateString(undefined, {
                            year: "numeric", month: "long", day: "numeric"
                        })} />
                        <Field label="Credential ID" value={data.certificate_id.slice(0, 8).toUpperCase()} />
                        <Field label="Enrollment" value={data.enrollment_id.slice(0, 8).toUpperCase()} />
                        <Field label="Status" value={
                            <span className="text-primary inline-flex items-center gap-1">
                                <BadgeCheck className="h-4 w-4" /> Valid
                            </span>
                        } />
                    </div>

                    <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
                        <Button
                            onClick={copyLink}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="verify-copy-link-btn"
                        >
                            <Share2 className="h-4 w-4 mr-2" />
                            Copy verify link
                        </Button>
                        <Button variant="outline" asChild data-testid="verify-explore-btn">
                            <Link to="/">
                                Explore programs
                                <ExternalLink className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                    </div>
                    {data.tagline && (
                        <p className="mt-10 text-xs italic text-muted-foreground">
                            {data.tagline}
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="border-l-2 border-accent/50 pl-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {label}
            </div>
            <div className="font-medium text-foreground mt-1">{value}</div>
        </div>
    );
}
